// Phase 38 · 23b — the KPI card.
//
// The argument this card exists to fix: on the live dashboard all six KPI tiles
// render identically, so 9.0% SLA compliance looks exactly like 86% CSAT and
// the worst number on the page is the easiest to miss. A card now carries a
// TONE derived from its own figure, and states the count behind the percentage
// where one is available.
//
// The tones are arithmetic, not targets. There is no tenant-wide SLA goal in
// the system to compare against, so inventing "below 90% is bad" would be
// printing a threshold nobody set. What the card can say without inventing
// anything is that MORE OBLIGATIONS WERE MISSED THAN MET — a majority, which is
// a fact about the number itself — and that a rate of exactly zero is a
// category rather than a small quantity.

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { SlaMetrics } from '@services/analytics.service';
import { slaBreachCount, zeroMeaning } from '../widgetFindings';
import { KpiDrillDownModal, hasKpiDrillDown, type KpiDrillDownContext } from './KpiDrillDownModal';

/** Props passed to every widget by the renderer */
export interface WidgetCommonProps {
  config: Record<string, any>;
  loading?: boolean;
  dashboardData?: any;
  /** SLA counts, so the compliance card can print the population behind its %. */
  slaData?: SlaMetrics;
  dateRange?: { start: string; end: string };
  isEditMode?: boolean;
  /** Set on the live Dashboard only: clicking the card lists its tickets. */
  drillDown?: KpiDrillDownContext;
}

type Tone = 'neutral' | 'warn' | 'danger';

interface Extracted {
  value: string | number;
  subtitle?: string;
  tone?: Tone;
}

const TONE: Record<Tone, { bg: string; border: string; ink: string; label: string }> = {
  neutral: {
    bg: 'var(--co-raised)',
    border: 'var(--co-line)',
    ink: 'var(--co-ink)',
    label: 'var(--co-ink-3)',
  },
  warn: {
    bg: 'var(--co-terracotta-wash)',
    border: 'var(--co-terracotta-line)',
    ink: 'var(--co-terracotta-ink)',
    label: 'var(--co-terracotta-ink)',
  },
  danger: {
    bg: 'var(--co-burgundy-wash)',
    border: 'var(--co-burgundy-line)',
    ink: 'var(--co-burgundy)',
    label: 'var(--co-burgundy)',
  },
};

const DEFAULT_TITLES: Record<string, string> = {
  kpi_open_tickets: 'Active Tickets',
};

/** Map widgetType → the figure, its subline and its tone. */
const KPI_CONFIGS: Record<string, (d: any, sla?: SlaMetrics) => Extracted> = {
  kpi_open_tickets: (d) => {
    const t = d?.tickets;
    if (!t) return { value: '—' };
    const open = (t.new || 0) + (t.in_progress || 0) + (t.pending || 0);
    if (open === 0) return { value: '—', subtitle: 'No open tickets' };
    const total = t.total || 0;
    return {
      value: open,
      subtitle: total > 0 ? `${Math.round((open / total) * 100)}% of all tickets are still open` : undefined,
    };
  },

  kpi_total_tickets: (d) => {
    const total = d?.tickets?.total;
    if (total == null) return { value: '—' };
    if (total === 0) return { value: '—', subtitle: 'No tickets yet' };
    return { value: total };
  },

  kpi_avg_response_time: (d) => {
    const ms = d?.performance?.avgFirstResponseTime;
    // 0 means nothing has been responded to, not an instant reply.
    if (ms == null) return { value: '—' };
    if (ms === 0) return { value: '—', subtitle: 'No responses yet' };
    if (ms < 60000) return { value: `${Math.round(ms / 1000)}s`, subtitle: 'First reply' };
    if (ms < 3600000) return { value: `${Math.round(ms / 60000)}m`, subtitle: 'First reply' };
    return { value: `${(ms / 3600000).toFixed(1)}h`, subtitle: 'First reply' };
  },

  kpi_sla_compliance: (d, sla) => {
    const v = d?.performance?.firstResponseSlaCompliance;
    if (v == null) return { value: '—' };
    // Majority missed is a statement about the figure, not a target someone set.
    const tone: Tone = v < 50 ? 'danger' : 'neutral';
    const counts = slaBreachCount(v, sla);
    const subtitle = counts
      ? `${counts.breached} of ${counts.total} breached`
      : v === 0
        ? zeroMeaning('kpi_sla_compliance') || undefined
        : 'First response targets';
    return { value: `${v.toFixed(1)}%`, subtitle, tone };
  },

  kpi_csat_score: (d) => {
    const v = d?.performance?.customerSatisfaction;
    if (v == null) return { value: '—' };
    if (v === 0) return { value: '—', subtitle: 'No ratings yet' };
    return { value: `${v.toFixed(1)}%`, subtitle: 'From the tickets that were rated' };
  },

  kpi_ai_resolution_rate: (d) => {
    const v = d?.performance?.aiResolutionRate;
    if (v == null) return { value: '—' };
    // This metric is always computed over the last 30 days regardless of the
    // dashboard's selected range, so disclose the window on the card.
    if (v === 0) {
      return {
        value: '0.0%',
        subtitle: zeroMeaning('kpi_ai_resolution_rate') || undefined,
        tone: 'warn',
      };
    }
    return { value: `${v.toFixed(1)}%`, subtitle: 'Last 30 days' };
  },
};

function titleFor(kpiType: string, configured?: string): string {
  if (configured) return configured;
  if (DEFAULT_TITLES[kpiType]) return DEFAULT_TITLES[kpiType];
  return kpiType.replace(/^kpi_/, '').replace(/_/g, ' ');
}

/** Below this the tile is one grid row: figure and subline only. */
const ROOMY_HEIGHT = 130;

/** Tracks whether the tile has room for the larger figure and the footer. */
function useRoomy() {
  const ref = useRef<HTMLDivElement>(null);
  const [roomy, setRoomy] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setRoomy(entry.contentRect.height >= ROOMY_HEIGHT));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, roomy };
}

export function KpiCardWidget({ config, loading, dashboardData, slaData, drillDown }: WidgetCommonProps) {
  const kpiType = config?.kpiType || 'kpi_open_tickets';
  const extract = KPI_CONFIGS[kpiType];
  const title = titleFor(kpiType, config?.title);
  const { ref, roomy } = useRoomy();
  const [open, setOpen] = useState(false);

  const extracted: Extracted =
    loading || !extract || !dashboardData ? { value: '—' } : extract(dashboardData, slaData);
  const tone = TONE[extracted.tone || 'neutral'];
  const drillable = !!drillDown && hasKpiDrillDown(kpiType);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <>
      <div
        ref={ref}
        {...(drillable
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-label': `${title}: ${extracted.value}. Show the tickets`,
              onClick: () => setOpen(true),
              onKeyDown,
            }
          : {})}
        className={`flex h-full flex-col overflow-hidden ${roomy ? 'px-[18px] py-[15px]' : 'px-[15px] py-[11px]'} ${
          drillable
            ? 'group/kpi cursor-pointer transition-shadow duration-150 hover:ring-1 hover:ring-inset hover:ring-[var(--co-brass)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--co-brass)]'
            : ''
        }`}
        style={{
          borderRadius: 10,
          border: `1px solid ${tone.border}`,
          background: tone.bg,
          boxShadow: 'var(--co-shadow-card)',
        }}
      >
        {/* The label and the figure are `flex-none`: a KPI tile is often only one
            grid row tall, and a shrinkable label in an overflowing flex column
            collapses to nothing — which is how five of these cards rendered with
            no title at all. The subline takes whatever height is left and clips
            at a line boundary rather than through a glyph. */}
        <div
          className="co-mono flex-none truncate text-[9.5px] font-semibold uppercase tracking-[.13em]"
          style={{ color: tone.label }}
        >
          {title}
        </div>

        {loading ? (
          <div
            className={`${roomy ? 'mt-[12px] h-[34px]' : 'mt-[7px] h-[26px]'} w-20 flex-none animate-pulse rounded bg-[var(--co-sunken)]`}
          />
        ) : (
          <div
            className={`co-mono flex-none font-semibold leading-none tracking-[-.02em] ${
              roomy ? 'mt-[12px] text-[34px]' : 'mt-[7px] text-[26px]'
            }`}
            style={{ color: tone.ink, fontVariantNumeric: 'tabular-nums' }}
          >
            {extracted.value}
          </div>
        )}

        {extracted.subtitle && !loading && (
          <div
            className={`${roomy ? 'mt-[10px] text-[12.5px]' : 'mt-[7px] text-[11.5px]'} min-h-0 leading-[1.35]`}
            title={extracted.subtitle}
            style={{
              color: extracted.tone && extracted.tone !== 'neutral' ? tone.ink : 'var(--co-ink-3)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {extracted.subtitle}
          </div>
        )}
      </div>

      {drillable && (
        <KpiDrillDownModal
          kpiType={kpiType}
          isOpen={open}
          onClose={() => setOpen(false)}
          dateRange={drillDown?.dateRange}
          teamId={drillDown?.teamId}
        />
      )}
    </>
  );
}
